import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  TextInput,
  NumberInput,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Text,
  Title,
  Flex,
  Select,
  SelectItem,
  Badge,
} from "@tremor/react";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import * as profilesAPI from "../../services/profiles";
import { CAPABILITY_TYPES, CAPABILITY_UNITS } from "./capability-options";
import useCatalogEntries from "./useCatalogEntries";
import {
  CAYENNE_SUPPORTED_TYPES,
  FIXED_DECODERS,
  catalogLabel,
  decoderLabel,
  decoderTypeError,
  labelFor,
} from "./type-labels";

const emptyRow = () => ({
  channel: null,
  name: "",
  type: "",
  unit: "",
});

const nextChannel = (rows) => {
  const maxCh = rows.reduce((m, r) => {
    const n = Number(r.channel);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return maxCh + 1;
};

function CayenneInfo() {
  return (
    <Card>
      <Title>CayenneLPP</Title>
      <Text className="mt-2">
        CayenneLPP is the most common LoRaWAN payload format. Zafron decodes
        incoming payloads automatically — whichever channels and types arrive
        in each uplink will appear on your dashboard. No setup needed here.
      </Text>
      <div className="mt-4">
        <Text className="font-medium mb-2">Types you can expect to see:</Text>
        <div className="flex flex-wrap gap-2">
          {CAYENNE_SUPPORTED_TYPES.map((t) => (
            <Badge key={t} color="blue">{labelFor(t)}</Badge>
          ))}
        </div>
      </div>
      <Text className="mt-4 italic">
        Want to sanity-check a real payload? Paste a sample on the Decoder → Test tab.
      </Text>
    </Card>
  );
}

function CatalogInfo({ type, entry, error, onRetry }) {
  if (!entry) {
    return (
      <Card>
        <Title>{type}</Title>
        {error ? (
          <div className="mt-2 flex items-center gap-3 rounded-lg bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            <span>We couldn't load the device catalog.</span>
            <Button size="xs" variant="secondary" onClick={onRetry}>Retry</Button>
          </div>
        ) : (
          <Text className="mt-2">
            This profile uses a built-in decoder. Its channel list is unavailable right now.
          </Text>
        )}
      </Card>
    );
  }
  return (
    <Card>
      <Title>{entry.title}</Title>
      <Text className="mt-2">{entry.description}</Text>
      <div className="mt-4">
        <Text className="font-medium mb-2">Channels this device reports:</Text>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Channel #</TableHeaderCell>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Type</TableHeaderCell>
              <TableHeaderCell>Unit</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entry.channels.map((c) => (
              <TableRow key={c.channel}>
                <TableCell>{c.channel}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{labelFor(c.type)}</TableCell>
                <TableCell>{c.unit}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Text className="mt-4 italic">
        Want to sanity-check a real payload? Paste a sample on the Decoder → Test tab.
      </Text>
    </Card>
  );
}

function CustomEditor({ profile, onSaved }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setRows((profile.capabilities || []).map((c) => {
      const n = c.channel === undefined || c.channel === null || c.channel === ""
        ? null
        : Number(c.channel);
      return {
        channel: Number.isFinite(n) ? n : null,
        name: c.name ?? "",
        type: c.type ?? "",
        unit: c.unit ?? "",
      };
    }));
  }, [profile]);

  const updateRow = (i, patch) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const addRow = () => setRows((prev) => [
    ...prev,
    { ...emptyRow(), channel: nextChannel(prev) },
  ]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const onSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const payload = rows
        .filter((r) => r.channel !== null && r.channel !== undefined && r.type !== "")
        .map((r) => ({
          channel: String(r.channel),
          name: r.name || undefined,
          type: r.type,
          unit: r.unit || undefined,
        }));
      const updated = await profilesAPI.update(profile._id, { capabilities: payload });
      if (onSaved) onSaved(updated);
    } catch (err) {
      setError(err.message || "Failed to save schema");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <Flex justifyContent="between" className="mb-4">
        <div>
          <Title>Custom decoder</Title>
          <Text>
            Custom decoders parse raw bytes into structured data. List the channels
            your device sends — channel number, display name, type slug
            (e.g., <code>air_temperature</code>), and unit. The Decoder tab uses
            this list to validate decoder output and to generate a starter scaffold.
          </Text>
        </div>
        <Button size="xs" icon={PlusIcon} onClick={addRow} variant="secondary">
          Add field
        </Button>
      </Flex>

      <datalist id="capability-type-options">
        {CAPABILITY_TYPES.map((t) => <option key={t} value={t} />)}
      </datalist>
      <datalist id="capability-unit-options">
        {CAPABILITY_UNITS.map((u) => <option key={u} value={u} />)}
      </datalist>

      {rows.length === 0 ? (
        <div className="text-center py-8 text-tremor-content dark:text-dark-tremor-content">
          <Text>No schema yet. Add a field to declare what your decoder emits.</Text>
        </div>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Channel #</TableHeaderCell>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Type</TableHeaderCell>
              <TableHeaderCell>Unit</TableHeaderCell>
              <TableHeaderCell></TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                <TableCell>
                  <NumberInput
                    value={row.channel ?? ""}
                    onValueChange={(v) => updateRow(i, { channel: v ?? null })}
                    placeholder="1"
                    step={1}
                    min={0}
                  />
                </TableCell>
                <TableCell>
                  <TextInput
                    value={row.name}
                    onValueChange={(v) => updateRow(i, { name: v })}
                    placeholder="Air temperature"
                  />
                </TableCell>
                <TableCell>
                  <input
                    type="text"
                    value={row.type}
                    onChange={(e) => updateRow(i, { type: e.target.value })}
                    placeholder="air_temperature"
                    list="capability-type-options"
                    className="w-full rounded-tremor-default border border-tremor-border dark:border-dark-tremor-border bg-tremor-background dark:bg-dark-tremor-background px-3 py-2 text-tremor-default text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis placeholder:text-tremor-content-subtle dark:placeholder:text-dark-tremor-content-subtle focus:border-tremor-brand-subtle dark:focus:border-dark-tremor-brand-subtle focus:outline-none focus:ring-2 focus:ring-tremor-brand-muted dark:focus:ring-dark-tremor-brand-muted"
                  />
                </TableCell>
                <TableCell>
                  <input
                    type="text"
                    value={row.unit}
                    onChange={(e) => updateRow(i, { unit: e.target.value })}
                    placeholder="c"
                    list="capability-unit-options"
                    className="w-full rounded-tremor-default border border-tremor-border dark:border-dark-tremor-border bg-tremor-background dark:bg-dark-tremor-background px-3 py-2 text-tremor-default text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis placeholder:text-tremor-content-subtle dark:placeholder:text-dark-tremor-content-subtle focus:border-tremor-brand-subtle dark:focus:border-dark-tremor-brand-subtle focus:outline-none focus:ring-2 focus:ring-tremor-brand-muted dark:focus:ring-dark-tremor-brand-muted"
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="xs"
                    variant="light"
                    icon={TrashIcon}
                    onClick={() => removeRow(i)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {error && (
        <div className="bg-red-100 dark:bg-red-950/40 px-2 py-1 mt-3">
          <Text className="text-red-600 dark:text-red-300">{error}</Text>
        </div>
      )}

      <Flex justifyContent="end" className="mt-4">
        <Button onClick={onSave} loading={saving}>Save schema</Button>
      </Flex>
    </Card>
  );
}

/**
 * Schema tab — top-level surface for the Profile settings page.
 *
 * Owns the decoder-type picker (auto-saves on change) and renders one of three
 * per-type sub-views: informational for built-ins (CayenneLPP and the device catalog),
 * editor for Custom.
 */
export default function SchemaTab({ profile, onSaved }) {
  const [savingType, setSavingType] = useState(false);
  const [typeError, setTypeError] = useState(null);
  const decoderType = profile?.decoder_type || "cayennelpp";
  const [pendingType, setPendingType] = useState(decoderType);
  const { entries: catalog, error: catalogError, reload: reloadCatalog } = useCatalogEntries();
  const [conflictProfileId, setConflictProfileId] = useState(null);

  const isCatalogType = !FIXED_DECODERS.some((d) => d.id === decoderType);

  // Reset the pending picker whenever the saved value changes (initial load,
  // external update, switching profiles). Without this, the picker would
  // keep stale state across profile reloads.
  useEffect(() => {
    setPendingType(decoderType);
    setTypeError(null);
  }, [decoderType]);

  // Avoid rendering CayenneLPP default content while the parent is still
  // loading the profile — the type would flash to the real value once fetched.
  if (!profile?._id) return null;

  const isDirty = pendingType !== decoderType;

  const onPickType = (type) => {
    setTypeError(null);
    setPendingType(type);
  };

  const onSaveType = async () => {
    if (!isDirty) return;
    setSavingType(true);
    setTypeError(null);
    setConflictProfileId(null);
    try {
      const updated = await profilesAPI.update(profile._id, { decoder_type: pendingType });
      if (onSaved) onSaved(updated);
    } catch (err) {
      const failure = decoderTypeError(err);
      setConflictProfileId(failure.conflict ? failure.profileId : null);
      setTypeError(failure.conflict
        ? `You already have a profile for ${decoderLabel(pendingType, catalog)}.`
        : failure.message);
    } finally {
      setSavingType(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <Title>Schema</Title>
        <Text className="mt-1">
          Pick how your device encodes uplinks. The schema describes what your dashboard can show.
        </Text>
        <div className="mt-4">
          <label className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
            Decoder type
          </label>
          <Flex justifyContent="start" alignItems="center" className="mt-1 gap-2">
            <Select
              value={pendingType}
              onValueChange={onPickType}
              disabled={savingType}
              className="max-w-xs"
            >
              {catalog.map((entry) => (
                <SelectItem key={entry.id} value={entry.id}>{catalogLabel(entry)}</SelectItem>
              ))}
              {FIXED_DECODERS.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
              ))}
              {isCatalogType && !catalog.some((e) => e.id === decoderType) && (
                <SelectItem value={decoderType}>{decoderType}</SelectItem>
              )}
            </Select>
            <Button
              onClick={onSaveType}
              loading={savingType}
              disabled={!isDirty || savingType}
            >
              Update
            </Button>
          </Flex>
          {catalogError && (
            <div className="mt-3 flex items-center gap-3 rounded-lg bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              <span>We couldn't load the device catalog.</span>
              <Button size="xs" variant="secondary" onClick={reloadCatalog}>Retry</Button>
            </div>
          )}
          {isDirty && !typeError && (
            <Text className="mt-2 text-xs text-amber-600 dark:text-amber-300">
              Unsaved change — click Update to apply.
            </Text>
          )}
          {typeError && (
            <div className="bg-red-100 dark:bg-red-950/40 px-2 py-1 mt-2">
              <Text className="text-red-600 dark:text-red-300">{typeError}</Text>
              {conflictProfileId && (
                <Link to={`/profiles/${conflictProfileId}/settings`} className="text-blue-600 dark:text-blue-400 text-sm">
                  Open that profile
                </Link>
              )}
            </div>
          )}
        </div>
      </Card>

      {decoderType === "cayennelpp" && <CayenneInfo />}
      {isCatalogType && (
        <CatalogInfo
          type={decoderType}
          entry={catalog.find((e) => e.id === decoderType)}
          error={catalogError}
          onRetry={reloadCatalog}
        />
      )}
      {decoderType === "custom" && (
        <CustomEditor profile={profile} onSaved={onSaved} />
      )}
    </div>
  );
}
